import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { BLOOD_TEST_PATH, BOARD_MANUAL_PATH, PATIENTS_PATH } from '../constants/routes';

export type Crumb = { label: string; to?: string };

/** パスに応じたパンくず */
export function crumbsForPathname(pathname: string): Crumb[] {
  const boardManual: Crumb = { label: '掲示板・マニュアル', to: BOARD_MANUAL_PATH };

  if (pathname === PATIENTS_PATH) {
    return [{ label: '患者一覧' }];
  }
  if (pathname === '/patients/new') {
    return [{ label: '患者一覧', to: PATIENTS_PATH }, { label: '新規患者受付' }];
  }
  const patientEditMatch = pathname.match(/^\/patients\/(\d+)\/edit$/);
  if (patientEditMatch) {
    const pid = patientEditMatch[1];
    return [
      { label: '患者一覧', to: PATIENTS_PATH },
      { label: '患者情報', to: `/patients/${pid}` },
      { label: '編集' },
    ];
  }
  const patientDetailMatch = pathname.match(/^\/patients\/(\d+)$/);
  if (patientDetailMatch) {
    return [{ label: '患者一覧', to: PATIENTS_PATH }, { label: '患者情報' }];
  }
  if (pathname === BOARD_MANUAL_PATH) {
    return [{ label: '掲示板・マニュアル' }];
  }
  if (pathname === BLOOD_TEST_PATH) {
    return [{ label: '採血結果解析' }];
  }
  if (pathname === '/profile') {
    return [{ label: 'プロフィール' }];
  }
  if (pathname === '/settings') {
    return [{ label: '設定' }];
  }
  if (pathname === '/staff') {
    return [{ label: '職員管理' }];
  }
  if (pathname === '/staff/new') {
    return [{ label: '職員管理', to: '/staff' }, { label: '職員を追加' }];
  }
  const staffEditMatch = pathname.match(/^\/staff\/(\d+)\/edit$/);
  if (staffEditMatch) {
    return [{ label: '職員管理', to: '/staff' }, { label: '職員を編集' }];
  }
  if (pathname === '/bulletin/new') {
    return [boardManual, { label: '掲示板' }, { label: '新規投稿' }];
  }
  const bulletinEditMatch = pathname.match(/^\/bulletin\/(\d+)\/edit$/);
  if (bulletinEditMatch) {
    const bid = bulletinEditMatch[1];
    return [
      boardManual,
      { label: '掲示板' },
      { label: '投稿', to: `/bulletin/${bid}` },
      { label: '編集' },
    ];
  }
  const bulletinDetailMatch = pathname.match(/^\/bulletin\/(\d+)$/);
  if (bulletinDetailMatch) {
    return [boardManual, { label: '掲示板' }, { label: '投稿' }];
  }
  if (pathname === '/manual/new') {
    return [boardManual, { label: '社内マニュアル' }, { label: '新規作成' }];
  }
  if (pathname === '/manual/groups') {
    return [boardManual, { label: '社内マニュアル' }, { label: 'グループ管理' }];
  }
  const manualEditMatch = pathname.match(/^\/manual\/(\d+)\/edit$/);
  if (manualEditMatch) {
    const mid = manualEditMatch[1];
    return [
      boardManual,
      { label: '社内マニュアル' },
      { label: 'マニュアル', to: `/manual/${mid}` },
      { label: '編集' },
    ];
  }
  const manualDetailMatch = pathname.match(/^\/manual\/(\d+)$/);
  if (manualDetailMatch) {
    return [boardManual, { label: '社内マニュアル' }, { label: 'マニュアル' }];
  }
  return [{ label: pathname }];
}

const AppBreadcrumbs: React.FC = () => {
  const { pathname } = useLocation();
  const crumbs = crumbsForPathname(pathname);

  return (
    <Breadcrumbs sx={{ '& .MuiBreadcrumbs-separator': { mx: 1 } }}>
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        if (isLast || !c.to) {
          return (
            <Typography key={`${c.label}-${i}`} color="text.primary" variant="body2" component="span">
              {c.label}
            </Typography>
          );
        }
        return (
          <Link
            key={`${c.label}-${i}`}
            component={RouterLink}
            to={c.to}
            underline="hover"
            color="inherit"
            variant="body2"
          >
            {c.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};

export default AppBreadcrumbs;
